import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "./Motion";

const NAV = [
  { label: "ONE+1™", hash: "one-plus-one", testid: "nav-link-oneplusone" },
  { label: "Tecnologia", to: "/tecnologia", testid: "nav-link-tecnologia" },
  { label: "Colori", hash: "colori", testid: "nav-link-colori" },
  { label: "Test Drive", to: "/test-drive", testid: "nav-link-testdrive" },
  { label: "Network", to: "/network", testid: "nav-link-network" },
  { label: "Guide", to: "/guide", testid: "nav-link-guide" },
  { label: "Hospitality", to: "/hospitality", testid: "nav-link-hospitality" },
];

export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function Header() {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const goHash = (hash) => {
    if (location.pathname !== "/") {
      navigate(`/#${hash}`);
    } else {
      scrollToSection(hash);
    }
    setOpen(false);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          solid || open ? "hdr-solid border-b border-white/10 bg-black/80 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-5 sm:h-[72px] sm:px-10">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src="/assets/logo-wordmark.webp" alt="SWAPA™" className="h-5 w-auto sm:h-6" />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigazione principale">
            {NAV.map((item) =>
              item.hash ? (
                <button
                  key={item.label}
                  data-testid={item.testid}
                  onClick={() => goHash(item.hash)}
                  className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 transition-colors duration-300 hover:text-white"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={item.testid}
                  className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-400 transition-colors duration-300 hover:text-white"
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/preordina"
              data-testid="nav-preorder-button"
              className="group hidden items-center gap-2 bg-swapa-yellow px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5 sm:flex"
            >
              Pre-ordina — €200
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className="flex h-10 w-10 items-center justify-center border border-white/15 text-white lg:hidden"
              aria-label={open ? "Chiudi menu" : "Apri menu"}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-ink px-6 pb-28 pt-28 lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Menu mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: EASE }}
                >
                  {item.hash ? (
                    <button
                      data-testid={`mobile-${item.testid}`}
                      onClick={() => goHash(item.hash)}
                      className="font-display text-4xl font-extrabold uppercase tracking-tight text-white"
                    >
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      to={item.to}
                      data-testid={`mobile-${item.testid}`}
                      className="font-display text-4xl font-extrabold uppercase tracking-tight text-white"
                    >
                      {item.label}
                    </Link>
                  )}
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6, ease: EASE }}
              className="flex flex-col gap-4"
            >
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Da €13.990 IVA inclusa</p>
              <Link
                to="/preordina"
                data-testid="mobile-preorder-button"
                className="flex items-center justify-center gap-2 bg-swapa-yellow py-4 font-mono text-sm font-semibold uppercase tracking-[0.18em] text-black"
              >
                Pre-ordina la tua SWAPA — €200
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
